/**
 * Repository pattern : couche d'abstraction pour Visites + BPU + Photos.
 *
 * - En ligne : axios direct + cache Dexie en arrière-plan
 * - Hors ligne : Dexie pur + enqueue d'actions
 *
 * Stratégie LWW : chaque entrée porte _updated_at_local (ISO) ; au push,
 * le serveur retourne la version officielle qui écrase la copie locale.
 */
import axios from 'axios';
import { offlineDB, enqueueAction, getMeta, setMeta } from './db';
import { processSyncQueue } from './sync';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const isOnline = () => typeof navigator === 'undefined' || navigator.onLine;
const nowIso = () => new Date().toISOString();

// ============== UUID generation (compatible standard RFC4122 v4) ==============
export function genId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback simple
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ============================ VISITES =========================================

export async function listVisits() {
  if (isOnline()) {
    try {
      const res = await axios.get(`${API}/visits`, { withCredentials: true });
      const fresh = res.data || [];
      // Cache, but preserve pending entries (they have _local_status='pending')
      await offlineDB.transaction('rw', offlineDB.visits, async () => {
        const pendings = await offlineDB.visits
          .where('_local_status').equals('pending').toArray();
        const pendingIds = new Set(pendings.map((p) => p.id));
        for (const v of fresh) {
          if (!pendingIds.has(v.id)) {
            await offlineDB.visits.put({ ...v, _local_status: 'synced' });
          }
        }
        // Remove cached entries that no longer exist on server (except pending)
        const allCached = await offlineDB.visits.toArray();
        const freshIds = new Set(fresh.map((v) => v.id));
        for (const c of allCached) {
          if (c._local_status !== 'pending' && !freshIds.has(c.id)) {
            await offlineDB.visits.delete(c.id);
          }
        }
        // Merge : return online list + offline-only pendings
        const offlineOnly = pendings.filter((p) => !freshIds.has(p.id));
        return [...fresh, ...offlineOnly];
      });
      const all = await offlineDB.visits.orderBy('_updated_at_local').reverse().toArray();
      return all.length ? all : fresh;
    } catch {
      // Fall back to cache if network failed mid-flight
      return offlineDB.visits.toArray();
    }
  }
  return offlineDB.visits.toArray();
}

export async function createVisit(payload) {
  const id = genId();
  const localDoc = {
    ...payload,
    id,
    _local_status: 'pending',
    _updated_at_local: nowIso(),
    created_at: nowIso(),
  };
  await offlineDB.visits.put(localDoc);
  if (isOnline()) {
    try {
      const res = await axios.post(`${API}/visits`, { ...payload, id }, { withCredentials: true });
      await offlineDB.visits.put({ ...res.data, _local_status: 'synced', _updated_at_local: nowIso() });
      return res.data;
    } catch (err) {
      // network error mid-flight → enqueue
      await enqueueAction({ entity: 'visits', action: 'create', entity_id: id, payload: { ...payload, id } });
      return localDoc;
    }
  }
  await enqueueAction({ entity: 'visits', action: 'create', entity_id: id, payload: { ...payload, id } });
  return localDoc;
}

export async function updateVisit(id, payload) {
  const localDoc = {
    ...payload,
    id,
    _local_status: 'pending',
    _updated_at_local: nowIso(),
  };
  await offlineDB.visits.put(localDoc);
  if (isOnline()) {
    try {
      await axios.put(`${API}/visits/${id}`, payload, { withCredentials: true });
      await offlineDB.visits.update(id, { _local_status: 'synced' });
      return { ...payload, id };
    } catch {
      await enqueueAction({ entity: 'visits', action: 'update', entity_id: id, payload });
      return localDoc;
    }
  }
  await enqueueAction({ entity: 'visits', action: 'update', entity_id: id, payload });
  return localDoc;
}

export async function deleteVisit(id) {
  await offlineDB.visits.delete(id);
  if (isOnline()) {
    try {
      await axios.delete(`${API}/visits/${id}`, { withCredentials: true });
      return { ok: true };
    } catch {
      await enqueueAction({ entity: 'visits', action: 'delete', entity_id: id, payload: {} });
      return { ok: true, queued: true };
    }
  }
  await enqueueAction({ entity: 'visits', action: 'delete', entity_id: id, payload: {} });
  return { ok: true, queued: true };
}

// ============================ PROPERTIES =====================================

export async function listProperties() {
  if (isOnline()) {
    try {
      const res = await axios.get(`${API}/properties`, { withCredentials: true });
      await setMeta('properties_cache', res.data || []);
      return res.data || [];
    } catch {
      const cached = await getMeta('properties_cache');
      return cached || [];
    }
  }
  const cached = await getMeta('properties_cache');
  return cached || [];
}

// ============================ BORDEREAU SEARCH (OFFLINE-AWARE) ===============

/**
 * Recherche locale dans le BPU (bordereau) — fallback offline pour
 * /api/bordereau/search. Filtre par catégorie + recherche textuelle.
 * NB: les items ont le champ `categorie` (français), pas `category`.
 */
export async function searchBordereauLocal({ q, category, limit = 15 }) {
  const doc = await getMeta('bordereau_doc');
  let items = (doc?.items || []);
  if (category) {
    const cat = String(category).toLowerCase();
    items = items.filter((i) => (i.categorie || i.category || '').toLowerCase() === cat);
  }
  if (q) {
    const ql = String(q).toLowerCase();
    // Normalize accents
    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
    const qn = norm(ql);
    const tokens = qn.split(/\s+/).filter(Boolean);
    items = items.filter((i) => {
      const lbl = norm(i.label || '');
      const tags = norm((i.tags || []).join(' '));
      const haystack = lbl + ' ' + tags;
      return tokens.every((t) => haystack.includes(t));
    });
  }
  return items.slice(0, limit);
}

/**
 * Template de visite (catégories elec/plomberie/etc.) offline-aware.
 * En ligne : /api/visits/template + cache. Hors ligne : reconstruit à partir du BPU caché.
 */
export async function getVisitTemplate() {
  if (isOnline()) {
    try {
      const res = await axios.get(`${API}/visits/template`, { withCredentials: true });
      await setMeta('visit_template_cache', res.data);
      return res.data;
    } catch {
      const cached = await getMeta('visit_template_cache');
      if (cached) return cached;
    }
  }
  // Offline fallback : on dérive les catégories du BPU
  const cached = await getMeta('visit_template_cache');
  if (cached) return cached;
  const doc = await getMeta('bordereau_doc');
  const items = doc?.items || [];
  const cats = Array.from(new Set(items.map((i) => i.categorie).filter(Boolean)));
  // Ordre canonique (mêmes corps que le seed backend)
  const canonical = [
    'Electricite', 'Plomberie', 'Chauffage / Climatisation', 'Toiture / Charpente',
    'Gros oeuvre / Structure', 'Isolation / Energetique', 'Menuiseries interieures',
    'Menuiseries exterieures', 'Revetements interieurs', 'Cuisine', 'Salle de bain / WC',
    'Exterieur / Facade', 'Conformite / Diagnostics',
  ];
  const ordered = [];
  for (const c of canonical) {
    if (cats.includes(c)) ordered.push({ name: c, items: [], hors_bordereau: [] });
  }
  for (const c of cats) {
    if (!canonical.includes(c)) ordered.push({ name: c, items: [], hors_bordereau: [] });
  }
  return { categories: ordered };
}

export async function searchBordereau({ q, category, limit = 15 }) {
  if (isOnline()) {
    try {
      const res = await axios.get(`${API}/bordereau/search`, {
        params: { q, category, limit },
        withCredentials: true,
      });
      return res.data?.items || [];
    } catch {
      return searchBordereauLocal({ q, category, limit });
    }
  }
  return searchBordereauLocal({ q, category, limit });
}

// ============================ STUDIES =========================================

export async function listStudies() {
  if (isOnline()) {
    try {
      const res = await axios.get(`${API}/studies`, { withCredentials: true });
      const fresh = res.data || [];
      await offlineDB.transaction('rw', offlineDB.studies, async () => {
        const pendings = await offlineDB.studies.where('_local_status').equals('pending').toArray();
        const pendingIds = new Set(pendings.map((p) => p.id));
        for (const s of fresh) {
          if (!pendingIds.has(s.id)) {
            await offlineDB.studies.put({ ...s, _local_status: 'synced' });
          }
        }
      });
      return fresh;
    } catch {
      return offlineDB.studies.toArray();
    }
  }
  return offlineDB.studies.toArray();
}

export async function createStudy(payload) {
  const id = genId();
  const localDoc = { ...payload, id, _local_status: 'pending', _updated_at_local: nowIso(), created_at: nowIso() };
  await offlineDB.studies.put(localDoc);
  if (isOnline()) {
    try {
      const res = await axios.post(`${API}/studies`, { ...payload, id }, { withCredentials: true });
      await offlineDB.studies.put({ ...res.data, _local_status: 'synced', _updated_at_local: nowIso() });
      return res.data;
    } catch {
      await enqueueAction({ entity: 'studies', action: 'create', entity_id: id, payload: { ...payload, id } });
      return localDoc;
    }
  }
  await enqueueAction({ entity: 'studies', action: 'create', entity_id: id, payload: { ...payload, id } });
  return localDoc;
}

export async function updateStudy(id, payload) {
  const localDoc = { ...payload, id, _local_status: 'pending', _updated_at_local: nowIso() };
  await offlineDB.studies.put(localDoc);
  if (isOnline()) {
    try {
      await axios.put(`${API}/studies/${id}`, payload, { withCredentials: true });
      await offlineDB.studies.update(id, { _local_status: 'synced' });
      return { ...payload, id };
    } catch {
      await enqueueAction({ entity: 'studies', action: 'update', entity_id: id, payload });
      return localDoc;
    }
  }
  await enqueueAction({ entity: 'studies', action: 'update', entity_id: id, payload });
  return localDoc;
}

/**
 * Calcul de l'étude en LOCAL (équivalent du calculate_study Python).
 * Permet de prévisualiser une étude en mode hors-ligne.
 * Reproduit fidèlement la logique backend.
 */
export function calculateStudyLocal(data, companySettings = {}) {
  const D = (k, def = 0) => Number(data[k] ?? def);
  const prix_achat = D('prix_achat');
  const frais_notaire = prix_achat * D('frais_notaire_pct') / 100;
  const frais_agence = D('frais_agence_achat');
  const cout_travaux_brut = D('travaux_estimation') * (1 + D('travaux_imprevus_pct') / 100);
  const honoraires_archi = D('travaux_estimation') * D('honoraires_archi_pct') / 100;
  const cout_travaux_total = cout_travaux_brut + honoraires_archi + D('assurance_dommages_ouvrage') + D('frais_geometre') + D('diagnostics_obligatoires');

  const finance_par = data.financement_mode || data.finance_par || 'emprunt';
  const apport = D('apport');
  const travaux_finances_par_emprunt = data.travaux_finances_par_emprunt !== false;
  let interets = 0, frais_bancaires = 0, assurance_emprunteur_total = 0, capital_emprunte = 0;
  if (finance_par === 'emprunt') {
    frais_bancaires = D('frais_dossier_banque') + D('frais_dossier') + D('frais_courtage') + D('frais_garantie');
    let base_emprunt = prix_achat + frais_notaire + frais_agence;
    if (travaux_finances_par_emprunt) base_emprunt += cout_travaux_total;
    capital_emprunte = Math.max(0, base_emprunt - apport);
    interets = capital_emprunte * (D('taux_emprunt_annuel') / 100) * (D('duree_portage_mois') / 12);
    assurance_emprunteur_total = D('assurance_emprunteur_mensuelle') * D('duree_portage_mois');
  }
  const cout_portage = interets + D('assurance_pno') + D('taxe_fonciere') + D('charges_copro') + assurance_emprunteur_total;

  const fraisFixesMensuels = Number(companySettings._frais_fixes_mensuels || 0);
  const nbPar = Math.max(1, Number(data.nb_projets_paralleles || companySettings.nb_projets_paralleles || 1));
  const dureePortage = Math.max(1, D('duree_portage_mois'));
  let quote_part_structure = Number(data.quote_part_structure || 0);
  if (!quote_part_structure && fraisFixesMensuels > 0) {
    quote_part_structure = (fraisFixesMensuels * dureePortage) / nbPar;
  }
  const cout_portage_total = cout_portage + quote_part_structure;
  const cout_total_acquisition = prix_achat + frais_notaire + frais_agence + cout_travaux_total + cout_portage_total + frais_bancaires;

  const frais_agence_revente = D('prix_revente_estime') * D('frais_agence_revente_pct') / 100;
  const frais_commercialisation = D('home_staging') + D('frais_publicite');
  const prix_net_revente = D('prix_revente_estime') - frais_agence_revente - frais_commercialisation;
  const plus_value_brute = prix_net_revente - cout_total_acquisition;

  let tva_montant = 0;
  if (data.regime_tva === 'marge' && plus_value_brute > 0) tva_montant = plus_value_brute * 0.20 / 1.20;
  else if (data.regime_tva === 'total') tva_montant = D('prix_revente_estime') * 0.20 / 1.20;

  const base_imposable = Math.max(0, plus_value_brute - tva_montant);
  let is_montant = 0;
  const taux_is = D('taux_is', 15);
  if (taux_is === 15) {
    if (base_imposable <= 42500) is_montant = base_imposable * 0.15;
    else is_montant = 42500 * 0.15 + (base_imposable - 42500) * 0.25;
  } else {
    is_montant = base_imposable * taux_is / 100;
  }
  const plus_value_nette = plus_value_brute - tva_montant - is_montant;
  const roi_brut_pct = cout_total_acquisition > 0 ? (plus_value_brute / cout_total_acquisition) * 100 : 0;
  const roi_net_pct = cout_total_acquisition > 0 ? (plus_value_nette / cout_total_acquisition) * 100 : 0;
  const monthly_interets = dureePortage > 0 ? interets / dureePortage : 0;
  const charges_mensuelles = monthly_interets + D('assurance_emprunteur_mensuelle') + D('assurance_pno') / 12 + D('taxe_fonciere') / 12 + D('charges_copro') / 12;
  const charges_societe_mensuelles = fraisFixesMensuels / nbPar;
  const total_mensuel_recurrent = charges_mensuelles + charges_societe_mensuelles;
  const travaux_cadence_moyenne = cout_travaux_total / dureePortage;
  // Trésorerie démarrage : selon mode de financement et toggle travaux
  let tresorerie_demarrage;
  if (finance_par === 'fonds_propres') {
    tresorerie_demarrage = (prix_achat + frais_notaire + frais_agence) + cout_travaux_total * 0.30 + frais_bancaires;
  } else {
    const acompte = travaux_finances_par_emprunt ? cout_travaux_total * 0.30 : cout_travaux_total;
    tresorerie_demarrage = apport + frais_bancaires + acompte;
  }
  const seuil_rentabilite = cout_total_acquisition / (1 - D('frais_agence_revente_pct') / 100);

  const round = (n) => Math.round(n * 100) / 100;
  return {
    frais_notaire: round(frais_notaire),
    cout_travaux_total: round(cout_travaux_total),
    cout_portage: round(cout_portage_total),
    frais_bancaires: round(frais_bancaires),
    capital_emprunte: round(capital_emprunte),
    interets_emprunt: round(interets),
    apport_utilise: round(apport),
    travaux_finances_par_emprunt: travaux_finances_par_emprunt,
    frais_commercialisation: round(frais_commercialisation),
    cout_total_acquisition: round(cout_total_acquisition),
    quote_part_structure: round(quote_part_structure),
    plus_value_brute: round(plus_value_brute),
    tva_montant: round(tva_montant),
    is_montant: round(is_montant),
    plus_value_nette: round(plus_value_nette),
    roi_brut_pct: round(roi_brut_pct),
    roi_net_pct: round(roi_net_pct),
    charges_mensuelles: round(charges_mensuelles),
    charges_societe_mensuelles: round(charges_societe_mensuelles),
    total_mensuel_recurrent: round(total_mensuel_recurrent),
    travaux_cadence_moyenne: round(travaux_cadence_moyenne),
    tresorerie_demarrage: round(tresorerie_demarrage),
    // Backward compat
    travaux_mensualises: round(travaux_cadence_moyenne),
    total_mensuel_previsionnel: round(total_mensuel_recurrent),
    seuil_rentabilite: round(seuil_rentabilite),
    _computed_offline: !isOnline(),
  };
}

// ============================ DIFFERENTIAL SYNC ===============================

export async function pullSinceLastSync() {
  if (!isOnline()) return null;
  const since = await getMeta('last_sync_at');
  try {
    const params = since ? `?since=${encodeURIComponent(since)}` : '';
    const res = await axios.get(`${API}/sync/since${params}`, { withCredentials: true });
    const { visits = [], studies = [], properties = [], bordereau, server_time } = res.data;
    await offlineDB.transaction('rw', offlineDB.visits, offlineDB.studies, async () => {
      const visitPendings = new Set(
        (await offlineDB.visits.where('_local_status').equals('pending').toArray()).map((p) => p.id)
      );
      const studyPendings = new Set(
        (await offlineDB.studies.where('_local_status').equals('pending').toArray()).map((p) => p.id)
      );
      for (const v of visits) {
        if (!visitPendings.has(v.id)) {
          await offlineDB.visits.put({ ...v, _local_status: 'synced' });
        }
      }
      for (const s of studies) {
        if (!studyPendings.has(s.id)) {
          await offlineDB.studies.put({ ...s, _local_status: 'synced' });
        }
      }
    });
    if (bordereau) {
      await setMeta('bordereau_doc', bordereau);
    }
    await setMeta('last_sync_at', server_time);
    return { visits: visits.length, studies: studies.length, properties: properties.length };
  } catch (e) {
    console.warn('Differential pull failed:', e);
    return null;
  }
}

// ============================ BORDEREAU (BPU) =================================

export async function getBordereau() {
  if (isOnline()) {
    try {
      const res = await axios.get(`${API}/bordereau`, { withCredentials: true });
      // Cache the doc as a single record at id='_main'
      await setMeta('bordereau_doc', res.data);
      return res.data;
    } catch {
      const cached = await getMeta('bordereau_doc');
      return cached || { items: [] };
    }
  }
  const cached = await getMeta('bordereau_doc');
  return cached || { items: [] };
}

export async function updateBordereau(items) {
  // Toujours s'assurer qu'une ligne possède un id (utile en mode hors-ligne pour
  // que l'autocomplete BPU et le matching BDC fonctionnent immédiatement).
  const itemsWithIds = (items || []).map((it) => (it && it.id ? it : { ...it, id: genId() }));
  const previous = (await getMeta('bordereau_doc')) || { items: [] };
  const doc = {
    ...previous,
    items: itemsWithIds,
    _local_status: 'pending',
    _updated_at_local: nowIso(),
  };
  await setMeta('bordereau_doc', doc);
  if (isOnline()) {
    try {
      const res = await axios.put(`${API}/bordereau`, { items: itemsWithIds }, { withCredentials: true });
      // Le backend renvoie maintenant la liste complète d'items (avec leurs IDs serveur).
      // On met à jour le cache offline pour que les nouvelles lignes soient
      // immédiatement disponibles si l'utilisateur passe hors-ligne juste après.
      const serverItems = Array.isArray(res.data?.items) ? res.data.items : itemsWithIds;
      await setMeta('bordereau_doc', {
        ...previous,
        items: serverItems,
        _local_status: 'synced',
        _updated_at_local: nowIso(),
      });
      return res.data;
    } catch {
      await enqueueAction({ entity: 'bordereau', action: 'update', entity_id: '_main', payload: { items: itemsWithIds } });
    }
  } else {
    await enqueueAction({ entity: 'bordereau', action: 'update', entity_id: '_main', payload: { items: itemsWithIds } });
  }
  return doc;
}

// ============================ PHOTOS ==========================================

/**
 * Compresse un blob image à max ~1MB (qualité 0.85, downscale si > 1920px).
 * Retourne un Blob compressé (jpeg).
 */
export async function compressImage(file, { maxDim = 1920, quality = 0.85, targetSizeMB = 1 } = {}) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        // Try iterative quality reduction to reach targetSize
        const tryAtQuality = (q) => new Promise((res) => {
          canvas.toBlob((b) => res(b), 'image/jpeg', q);
        });
        (async () => {
          let q = quality;
          let blob = await tryAtQuality(q);
          while (blob && blob.size > targetSizeMB * 1024 * 1024 && q > 0.4) {
            q -= 0.1;
            blob = await tryAtQuality(q);
          }
          resolve(blob);
        })();
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Stocke une photo localement. La structure :
 *  { id (auto), visit_id, project_id, blob, mime_type, filename, folder, subfolder,
 *    _local_status: 'pending', server_file_id?: <retour du serveur après upload>,
 *    created_at }
 * Retourne l'id local pour permettre l'affichage immédiat.
 */
export async function storePhotoLocal({ file, visit_id, project_id, folder, subfolder, filename }) {
  const blob = await compressImage(file).catch(() => file);
  const photo = {
    visit_id: visit_id || null,
    project_id: project_id || null,
    blob,
    mime_type: blob.type || 'image/jpeg',
    filename: filename || file.name || `photo_${Date.now()}.jpg`,
    folder: folder || 'visites',
    subfolder: subfolder || 'Photos visite',
    _local_status: 'pending',
    created_at: nowIso(),
  };
  const id = await offlineDB.photos.add(photo);
  await enqueueAction({ entity: 'photo', action: 'upload_photo', entity_id: id, payload: {} });
  // Immediate sync attempt if online
  if (isOnline()) {
    processSyncQueue().catch(() => null);
  }
  return { ...photo, id };
}

export async function getPhotosForVisit(visit_id) {
  return offlineDB.photos.where('visit_id').equals(visit_id).toArray();
}

export function blobToObjectURL(blob) {
  return URL.createObjectURL(blob);
}
